import { defineTextStyles } from '@chakra-ui/react';
import { typography } from './typography';

export const textStyles = defineTextStyles({
  // Body text styles
  body: {
    description:
      'The default body text style - used for general paragraphs and content.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.md,
      lineHeight: typography.lineHeights.normal,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'none',
    },
  },

  'body.muted': {
    description:
      'Muted body text - used for secondary or less prominent content.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.normal,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'none',
    },
  },
  'body.small': {
    description:
      'Small body text - used for footnotes, disclaimers, or small content.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.xs,
      lineHeight: typography.lineHeights.shorter,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'none',
    },
  },

  // Heading styles
  heading: {
    description:
      'The base heading text style - used for general titles and headings.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes['2xl'],
      lineHeight: typography.lineHeights.short,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'heading.main': {
    description:
      'Main heading style - used for primary titles, such as page titles.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.extrabold,
      fontSize: typography.fontSizes['2xl'],
      lineHeight: typography.lineHeights.short,
      letterSpacing: '-0.02em',
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'heading.sub': {
    description:
      'Subheading style - used for secondary titles or section headers.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.semibold,
      fontSize: typography.fontSizes.xl,
      lineHeight: typography.lineHeights.short,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'heading.section': {
    description:
      'Section heading style - used for smaller sections or subsections.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.medium,
      fontSize: typography.fontSizes.md,
      lineHeight: typography.lineHeights.short,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'heading.card': {
    description:
      'Section heading style - used for smaller sections or subsections.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.md,
      lineHeight: typography.lineHeights.short,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },

  'heading.tab': {
    description: 'Tab heading style - used for tab labels.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.short,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'heading.table': {
    description: 'Table heading style - used for table column headers.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.extrabold,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.short,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'capitalize',
      textOverflow: 'ellipsis',
    },
  },
  'heading.form': {
    description: 'Form heading style - used for form labels.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.extrabold,
      fontSize: typography.fontSizes.lg,
      lineHeight: typography.lineHeights.normal,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },

  // Label styles
  fieldLabel: {
    description: 'Field label style - used for form labels or field titles.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.medium,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.normal,
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'fieldLabel.bold': {
    description: 'Bold field label style - used for emphasized form labels.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.extrabold,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.normal,
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'fieldLabel.muted': {
    description:
      'Muted field label style - used for less prominent form labels.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.normal,
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  'fieldLabel.description': {
    description:
      'Description field label style - used for less prominent form labels.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.xs,
      lineHeight: typography.lineHeights.normal,
      textDecoration: 'none',
      textTransform: 'none',
    },
  },
  'fieldLabel.descriptionBold': {
    description:
      'Description field label style - used for less prominent form labels.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.xs,
      lineHeight: typography.lineHeights.normal,
      textDecoration: 'none',
      textTransform: 'none',
    },
  },
  'fieldLabel.items': {
    description: 'Item field label style - used for item form labels.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.semibold,
      fontSize: typography.fontSizes['xs'],
      lineHeight: typography.lineHeights.short,
      textDecoration: 'none',
      textTransform: 'capitalize',
    },
  },
  fieldContent: {
    description: 'Field content style - used for form labels or field titles.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.normal,
      letterSpacing: '-0.025em',
      textDecoration: 'none',
      textTransform: 'none',
    },
  },
  floatingLabel: {
    description: 'Floating label style - used for floating labels.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.medium,
      fontSize: typography.fontSizes['3xs'],
      lineHeight: typography.lineHeights.short,
      textDecoration: 'none',
      textTransform: 'none',
      letterSpacing: '-0.025em',
    },
  },

  // Caption styles
  caption: {
    description:
      'Caption text style - used for small annotations or helper text.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.xs,
      lineHeight: typography.lineHeights.shorter,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'none',
    },
  },
  'caption.muted': {
    description:
      'Muted caption text style - used for less prominent annotations.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes['2xs'],
      lineHeight: typography.lineHeights.shorter,
      letterSpacing: '0',
      textDecoration: 'none',
      textTransform: 'none',
    },
  },

  // Button text styles
  button: {
    description: 'Button text style - used for text inside buttons.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.semibold,
      fontSize: typography.fontSizes['xs'],
      lineHeight: typography.lineHeights.normal,
      textDecoration: 'none',
      textTransform: 'capitalize',
      letterSpacing: '0.05em',
    },
  },

  // Table text styles
  'table.header': {
    description: 'Table header text style - used for table column headers.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.shorter,
      textDecoration: 'none',
      textTransform: 'capitalize',
      letterSpacing: '0',
    },
  },
  'table.cell': {
    description: 'Table cell text style - used for table content.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.normal,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.normal,
      textDecoration: 'none',
      textTransform: 'none',
    },
  },

  'chart.title': {
    description: 'Chart title text style - used for chart titles.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.md,
      lineHeight: typography.lineHeights.normal,
    },
  },
  'chart.subtitle': {
    description: 'Chart subtitle text style - used for chart subtitles.',
    value: {
      fontFamily: typography.fonts.heading,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.sm,
      lineHeight: typography.lineHeights.normal,
    },
  },
  'chart.value': {
    description: 'Chart value text style - used for chart values.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.xs,
      lineHeight: typography.lineHeights.normal,
    },
  },
  'chart.label': {
    description: 'Chart label text style - used for chart labels.',
    value: {
      fontFamily: typography.fonts.body,
      fontWeight: typography.fontWeights.bold,
      fontSize: typography.fontSizes.xs,
      lineHeight: typography.lineHeights.normal,
    },
  },
});
